/* eslint-disable consistent-return */
/**
 *
 * Client Exclusive Card
 *
 */

import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import { useDispatch, useSelector } from 'react-redux';
import Typography from '@material-ui/core/Typography';
import useZoomLevel from 'components/Hooks/useZoomLevel';
import Card from 'components/Card';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { setLeavingModal } from 'containers/Main/actions';
import { makeSelectClientDetails } from 'containers/Main/selectors';
import { makeSelectProfile } from 'containers/Auth/selectors';
import SubscriptionService from 'utils/subscriptionService';
import { fallbackImage, getImageFile, isIE } from 'utils/stringUtils';
import ClassNames from 'classnames';
import CardActionArea from '@material-ui/core/CardActionArea';
import { useHistory, useLocation } from 'react-router-dom';
import Mixpanel, { injectAssessmentResultsABData } from 'utils/mixpanelService';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { getMatchedLabels } from 'components/ResourceItemSmall/utils';
import useSiteCopySelector from 'components/useSiteCopySelector';
import useAlgoliaLocale from 'components/useAlgoliaLocale';
import AssessmentResultContext from 'containers/AssessmentResult/AssessmentResultContext';
import SurveyResultContext from 'components/SurveyResult/SurveyResultContext';
import { analyzeClientResource } from 'utils/analyzeClientResource';
import { getAlgoliaLocalizedField } from 'utils/localeUtils';
import LinkItemHybrid from 'components/LinkItemSmall/LinkItemHybrid';
import LinkItemMultiCta from 'components/LinkItemSmall/LinkItemMultiCta';
import { getAccentColor } from 'containers/Main/utils';
import RightArrow from './images/blue-right-arrow.svg';
import NewTabLink from './images/new-tab.svg';
import { getTopicsTitles, getThemesTitles, getEventPageName } from './utils';

const calcClampHeight = (fontSizeRem, lineClamp, lineHeight = 1.5) =>
  `calc(${fontSizeRem} * ${lineHeight} * ${lineClamp})`;

const useStyles = makeStyles(theme => ({
  image: {
    display: 'block',
    width: '100%',
    height: props => (props.fromSuggestions ? 92 : 'auto'),
    position: 'relative',
    overflow: 'hidden',
    padding: '56.25% 0 0 0',
    border: props => props.fromSuggestions && '1px solid #E9ECED',
    borderBottom: props => !props.fromSuggestions && '1px solid #E9ECED',
  },
  matchedLabel: {
    zIndex: 2,
    position: 'absolute',
    bottom: 12,
    left: 12,
    padding: '4px 8px',
    borderRadius: 12,
    backgroundColor: '#000000B3',
    color: '#FFFFFF',
    ...theme.typography.pxSmall,
  },
  originalImage: {
    display: 'block',
    width: '100%',
    height: 'auto',
    margin: 'auto',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  },
  borderedImage: {
    borderTopLeftRadius: props => !props.fromSuggestions && 8,
    borderTopRightRadius: props => !props.fromSuggestions && 8,
  },
  article: {
    display: '-webkit-box',
    marginTop: props => (props.fromSuggestions ? 10 : 17),
    '-webkit-line-clamp': props => !props.fromSuggestions && '3',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: props =>
      props.fromSuggestions ? 12 : props.zoomed ? '0.8rem' : '0.9rem',
    lineHeight: 1.5,
    fontFamily: props =>
      props.fromSuggestions ? 'Poppins' : 'PoppinsSemiBold',
    fontWeight: props => props.fromSuggestions && 500,
    cursor: 'pointer',
    height: props =>
      !props.fromSuggestions &&
      calcClampHeight(props.zoomed ? '0.8rem' : '0.9rem', 3),
    [theme.breakpoints.down('xs')]: {
      marginTop: 14,
      '-webkit-line-clamp': 2,
      fontSize: props => (props.zoomed ? '0.8rem' : '0.875rem'),
      height: props => calcClampHeight(props.zoomed ? '0.8rem' : '0.875rem', 2),
    },
    '&.no-truncate': {
      display: 'block',
      height: 'auto',
    },
  },
  biggerCardArticle: {
    display: '-webkit-box',
    '-webkit-line-clamp': 3,
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: props => (props.zoomed ? '0.8rem' : '0.9rem'),
    lineHeight: 1.5,
    height: props => calcClampHeight(props.zoomed ? '0.8rem' : '0.9rem', 3),
  },
  btnWrapper: {
    paddingLeft: 20,
    display: 'flex',
    paddingBottom: 15,
  },
  actionBtn: {
    border: '1px solid #E1E1E1',
    borderRadius: 2,
    width: 108,
    height: 34,
    ...theme.typography.pxSmall,
    color: theme.palette.primary.main,
    lineHeight: 'inherit',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  linkIcon: {
    height: 12,
    marginRight: 5,
    marginBottom: 1,
  },
  itemWrapper: {
    position: 'relative',
    height: '100%',
  },
  indicator: {
    height: 5,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
  },
  description: {
    display: '-webkit-box',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    marginTop: 2,
    lineHeight: 1.5,
    textOverflow: 'ellipsis',
    fontSize: props => (props.fromSuggestions ? 12 : '0.8rem'),
    height: props => !props.fromSuggestions && 40,
    '&.no-truncate': {
      display: 'block',
      height: 'auto',
    },
  },
  ctaContainer: {
    display: 'flex',
    alignItems: 'center',
    margin: props => (props.fromSuggestions ? '6px 0' : '20px 0'),
    height: props => !props.fromSuggestions && calcClampHeight('0.75rem', 3),
    '& img': {
      marginLeft: 5,
    },
  },
  cta: {
    display: '-webkit-box',
    '-webkit-line-clamp': 3,
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    fontSize: props => (props.zoomed ? '0.6rem' : '0.75rem'),
    lineHeight: 1.5,
    textOverflow: 'ellipsis',
    maxHeight: props => calcClampHeight(props.zoomed ? '0.6rem' : '0.75rem', 3),
    '&.no-truncate': {
      height: 'auto',
    },
  },
  shortCta: {
    margin: '10px 0 15px 0',
  },
  item: {
    width: '100%',
    padding: props => !props.fromSuggestions && '0 20px',
  },
  topicItem: {
    padding: '0 14px',
  },
  cardLink: {
    textDecoration: 'none',
  },
  contentContainer: {
    height: '100%',
  },
  grayscale: {
    filter: 'grayscale(1)',
  },
}));

function ClientExclusiveCard(props) {
  const {
    item,
    onClick,
    fromSuggestions,
    page = 'Home',
    pageShortName,
    isBiggerCard,
    isPdf,
    grayscale,
    truncate = true,
    eventProperties,
    isPreview = false,
  } = props;
  const dispatch = useDispatch();
  const [siteCopy] = useSiteCopySelector(['resource-item-page']);
  const locale = useAlgoliaLocale();
  const { zoom } = useZoomLevel();
  const { answersMatchedAudiences, answersMatchedTags } = useContext(
    SurveyResultContext,
  );
  const { shouldABTest, isVariantB } = useContext(AssessmentResultContext);
  const clientDetails = useSelector(makeSelectClientDetails());
  const profile = useSelector(makeSelectProfile());
  const profileFilters = useSelector(state => state.auth.profileFilters);
  const siteConfig = useSelector(state => state.main.siteConfig);
  const profileTags = _get(profileFilters, 'tags', []);
  const classes = useStyles({ zoomed: zoom > 100, fromSuggestions });
  const location = useLocation();
  const history = useHistory();

  const isTopicPage = page === 'Topic';
  const { __typename } = item;

  const isLink =
    _get(item, 'sys.contentType.sys.id') === 'link' ||
    __typename === 'Link' ||
    item.type === 'Links';

  // separate the logic for preview to simplify

  const isExpandedLayout = isAdminPortal => {
    if (isAdminPortal) {
      return !_get(item, 'displaySimpleCard');
    }

    return !_get(clientDetails, 'metadata.displaySimpleCard');
  };

  const isMultiCtaLayout = isAdminPortal => {
    if (isAdminPortal) {
      return false;
    }

    const isMultiCtaContactInfoType = ['Copy', 'Link'].includes(
      _get(item, 'contactInfoType'),
    );

    return (
      _get(clientDetails, 'metadata.enableMultiCtaCard') &&
      isMultiCtaContactInfoType
    );
  };

  if (isLink && isMultiCtaLayout(isPreview)) {
    return <LinkItemMultiCta {...props} />;
  }

  if (!fromSuggestions && isLink && isExpandedLayout(isPreview)) {
    return <LinkItemHybrid {...props} />;
  }

  const matchedLabels = getMatchedLabels(
    item,
    answersMatchedAudiences,
    answersMatchedTags,
  );
  const accentColor = getAccentColor(clientDetails, null);
  const showSubtitle =
    ['Home', 'Topic', 'User-Resources'].includes(page) || fromSuggestions;
  const {
    article,
    name,
    title,
    type,
    slug,
    callToAction,
    exitDisclaimer,
    topics,
    theme,
    themes,
  } = item;

  const { isInternal, url } = analyzeClientResource(
    item,
    clientDetails,
    profileTags,
  );

  const showAsLink = isIE() && !isInternal && url;

  const cardTitle =
    getAlgoliaLocalizedField(item, 'article', locale) ||
    getAlgoliaLocalizedField(item, 'name', locale) ||
    getAlgoliaLocalizedField(item, 'title', locale);

  const redirect = () => {
    const entryId = item.objectID || item.sys.id;

    if (onClick) {
      onClick({
        type: item.type,
        name: item.nameTitle,
        entryId,
      });
    }
    if (!url && !isInternal) return history.push('/404');

    const clientName = clientDetails.name;

    const finalPageName = getEventPageName({
      pageName: page,
      pageShortName,
    });

    const themesTitles = getThemesTitles({ theme, themes });
    const topicsTitles = getTopicsTitles({ topics });

    const themesAndTopics = {
      ...(!_isEmpty(themesTitles) ? { themes: themesTitles } : {}),
      ...(!_isEmpty(topicsTitles) ? { topics: topicsTitles } : {}),
    };

    if (isInternal) {
      Mixpanel.track(
        `Client Resource Card Clicked - ${page}`,
        injectAssessmentResultsABData(
          {
            article: article || name || title,
            page: finalPageName,
            slug,
            client: clientName,
            entryId,
            title: cardTitle,
            ...themesAndTopics,
            ...(eventProperties || {}),
          },
          shouldABTest,
          isVariantB,
        ),
      );

      const searchParams = new URLSearchParams(location.search);
      const filters = searchParams.get('filters');

      history.push(url, {
        filters,
      });
    }
    if (showAsLink) return false;
    if (!isInternal) {
      Mixpanel.track(
        `Client Resource Card Exited - ${page}`,
        injectAssessmentResultsABData(
          {
            page: finalPageName,
            exitPage: url,
            client: clientName,
            entryId,
            title: cardTitle,
            ...themesAndTopics,
            ...(eventProperties || {}),
          },
          shouldABTest,
          isVariantB,
        ),
      );

      const finalUrl = url.includes('http') ? url : `https://${url}`;
      if (!finalUrl.includes('assets.ctfassets.net')) {
        dispatch(
          setLeavingModal({
            visible: true,
            title:
              getAlgoliaLocalizedField(item, 'article', locale) ||
              getAlgoliaLocalizedField(item, 'title', locale) ||
              getAlgoliaLocalizedField(item, 'name', locale),
            url: finalUrl,
            organization: clientDetails.name,
            resourceType: type || null,
            disclaimer: exitDisclaimer,
            entryId: item?.objectID || item?.sys?.id,
            ...themesAndTopics,
          }),
        );
      } else {
        const userEmail =
          _get(profile, 'email') || _get(profile, 'personalEmail');
        const clientShortName = _get(clientDetails, 'shortName');
        const clientGroupShortName = _get(
          clientDetails,
          'clientGroup.shortName',
        );

        if (userEmail && url) {
          SubscriptionService.sendClientResourceTag({
            email: userEmail,
            url,
            tagType: 'appointment-clicked',
            siteConfig,
            clientShortName,
            clientGroupShortName,
          }).catch(error => {
            if (process.env.NODE_ENV === 'development') {
              console.error('Failed to send appointment tag to AC:', error);
            }
          });
          SubscriptionService.sendHCSCProviderTag({
            email: userEmail,
            resourceId: item?.objectID || item?.sys?.id,
            clientShortName,
            clientGroupFormalShortName: _get(
              clientDetails,
              'clientGroup.formalShortName',
            ),
            siteConfig,
          }).catch(error => {
            if (process.env.NODE_ENV === 'development') {
              console.error('Failed to send HCSC provider tag to AC:', error);
            }
          });
        }
        window.open(finalUrl, '_blank', 'noopener');
      }
    }
  };

  const handleTextClick = (e, titleType) => {
    if (titleType === 'title') {
      if (
        e.currentTarget.scrollHeight - 1 > e.currentTarget.offsetHeight ||
        e.currentTarget.classList.contains('truncate')
      ) {
        e.preventDefault();
        e.stopPropagation();
        e.currentTarget.classList.toggle('truncate');
      }
    } else if (
      e.currentTarget.scrollHeight - 4 > e.currentTarget.offsetHeight ||
      e.currentTarget.classList.contains('truncate-subtitle')
    ) {
      e.preventDefault();
      e.stopPropagation();
      e.currentTarget.classList.toggle('truncate-subtitle');
    }
  };

  const file = getImageFile(item, props.disableEmbedly);
  const customClientCtaLabel = _get(clientDetails, 'metadata.customLinkCTA');
  const ctaText =
    !url &&
    !callToAction &&
    (__typename === 'Source' ||
      _get(item, 'sys.contentType.sys.id') === 'source')
      ? customClientCtaLabel ||
        _get(siteCopy, 'pageCopy.clientExclusiveWaitLinkLabel')
      : customClientCtaLabel ||
        callToAction ||
        _get(siteCopy, 'pageCopy.clientExclusiveLearnMoreLabel');

  const content = () => (
    <Card
      background={isTopicPage ? '#F8F8F8' : '#fff'}
      padding={0}
      borderRadius={!fromSuggestions && 8}
      borderColor={!fromSuggestions && '#E9ECED'}
      mb={isTopicPage ? 20 : 0}
      width={isTopicPage ? '100%' : ''}
      height={isBiggerCard ? 271 : null}
    >
      <CardActionArea
        onClick={() => redirect(item)}
        className={classes.itemWrapper}
        disableRipple
      >
        <Grid container className={classes.contentContainer}>
          {accentColor && (
            <Grid
              item
              xs={12}
              className={classes.indicator}
              style={{ background: `#${accentColor}` }}
            />
          )}
          <Grid item xs={12} className={classes.image}>
            {isPdf ? (
              <img
                src={file}
                onError={e => {
                  e.target.src = fallbackImage;
                }}
                alt=""
                className={ClassNames(
                  classes.originalImage,
                  !accentColor ? classes.borderedImage : null,
                )}
              />
            ) : (
              <LazyLoadImage
                src={file}
                onError={e => {
                  e.target.src = fallbackImage;
                }}
                alt=""
                className={ClassNames(classes.originalImage, {
                  [classes.borderedImage]: !accentColor,
                  [classes.grayscale]: grayscale,
                })}
              />
            )}
            {!_isEmpty(matchedLabels) && (
              <Typography component="span" className={classes.matchedLabel}>
                {matchedLabels[0]}
              </Typography>
            )}
          </Grid>
          <Grid
            item
            xs={12}
            className={ClassNames(classes.item, {
              [classes.topicItem]: isTopicPage,
            })}
          >
            <Typography
              component="b"
              color="textPrimary"
              align="left"
              className={ClassNames(classes.article, {
                [classes.biggerCardArticle]: isBiggerCard,
                'no-truncate': !truncate,
              })}
              name="title"
              onClick={e => handleTextClick(e, 'title')}
            >
              {cardTitle}
            </Typography>
          </Grid>
          {showSubtitle && (
            <Grid
              item
              xs={12}
              className={ClassNames(classes.item, {
                [classes.topicItem]: isTopicPage,
              })}
            >
              <Typography
                align="left"
                color="textPrimary"
                variant="overline"
                name="subtitle"
                className={ClassNames(classes.description, {
                  'no-truncate': !truncate,
                })}
                onClick={e => handleTextClick(e, 'subtitle')}
              >
                {getAlgoliaLocalizedField(item, 'shortDescription', locale) ||
                  getAlgoliaLocalizedField(item, 'subtitle', locale)}
              </Typography>
            </Grid>
          )}

          {!fromSuggestions && (
            <Grid
              item
              xs={12}
              className={ClassNames(classes.item, {
                [classes.topicItem]: isTopicPage,
              })}
            >
              <Grid className={classes.ctaContainer}>
                <Typography
                  align="left"
                  color="textSecondary"
                  className={ClassNames(classes.cta, {
                    [classes.shortCta]: page === 'Home',
                    'no-truncate': !truncate,
                  })}
                >
                  {ctaText}
                </Typography>
                <img src={isInternal ? RightArrow : NewTabLink} alt="" />
              </Grid>
            </Grid>
          )}
        </Grid>
      </CardActionArea>
    </Card>
  );
  if (showAsLink || isPdf)
    return (
      <a
        href={url}
        target="_blank"
        aria-label={`Open ${cardTitle} in a new tab`}
        className={classes.cardLink}
      >
        {content()}
      </a>
    );
  return content();
}

ClientExclusiveCard.propTypes = {
  item: PropTypes.object,
  clientData: PropTypes.object,
  page: PropTypes.string,
  isBiggerCard: PropTypes.bool,
  isPdf: PropTypes.bool,
  fromSuggestions: PropTypes.bool,
  onClick: PropTypes.func,
  grayscale: PropTypes.bool,
  isPreview: PropTypes.bool,
};

export default React.memo(ClientExclusiveCard, (prevProps, nextProps) => {
  if (nextProps.isPreview) return false; // Return false to always re-render when isPreview is true
  // If event properties change, re-render
  if (_get(prevProps, 'eventProperties') !== _get(nextProps, 'eventProperties'))
    return false;

  if (_get(prevProps, 'item.imageUrl') !== _get(nextProps, 'item.imageUrl'))
    return false;

  if (_get(prevProps, 'item.title') !== _get(nextProps, 'item.title'))
    return false;

  if (_get(prevProps, 'item.article') !== _get(nextProps, 'item.article'))
    return false;

  if (_get(prevProps, 'item.name') !== _get(nextProps, 'item.name'))
    return false;

  if (_get(prevProps, 'isPdf') !== _get(nextProps, 'isPdf')) return false;

  return _get(prevProps, 'item.slug') === _get(nextProps, 'item.slug');
});
